import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import styles from '../internal_transfer/style.module.scss';
import { PlusIcon, ProjectNameIcon, StatusCheckIcon, EyeIcon } from 'assets';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import GenericTable from 'shared/components/common/table/table';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import Heading from 'shared/components/common/heading/Heading';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import { GetAllReturnOrderQuery } from 'shared/services/return_service/ReturnService';
import moment from 'moment';
import { routeConstant } from 'shared/routes/RouteConstant';
import { isRestrictActions } from 'shared/utils/Helper';
import { screenActionEnum, screenEnum } from 'shared/utils/Enum';
import PoSearchBar from 'shared/components/common/searchbar/PoSearchBar';
import ViewReturnModal from './ViewReturnModal';

const IGNORED_KEYS = new Set(['_id', '__v', 'createdAt', 'updatedAt', 'created_at', 'updated_at', 'po']);

const deepSearchSingleWord = (obj, word) => {
    if (typeof obj === 'string' || typeof obj === 'number') {
        return obj.toString().toLowerCase().includes(word);
    }

    if (Array.isArray(obj)) {
        return obj.some(item => deepSearchSingleWord(item, word));
    }

    if (typeof obj === 'object' && obj !== null) {
        return Object.entries(obj).some(([key, value]) => {
            if (IGNORED_KEYS.has(key)) {
                return false;
            }
            return deepSearchSingleWord(value, word);
        });
    }

    return false;
};

const deepSearch = (obj, searchTerm) => {
    if (!searchTerm) return true;
    
    // Parse search terms supporting quoted phrases
    const regex = /"([^"]+)"|'([^']+)'|(\S+)/g;
    const words = [];
    let match;
    while ((match = regex.exec(searchTerm)) !== null) {
        const token = match[1] || match[2] || match[3];
        if (token) {
            words.push(token.toLowerCase());
        }
    }

    if (words.length === 0) return true;
    return words.every(word => deepSearchSingleWord(obj, word));
};

export default function ReturnOrder() {
    const navigate = useNavigate();
    const { theme: { theme } } = useSelector((state) => state.root);
    const [searchMenu, setsearchMenu] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);

    const { isLoading, data: returnList } = GetAllReturnOrderQuery();

    useEffect(() => {
        if (returnList) {
            setSearchResults(returnList);
        }
    }, [returnList]);

    const SearchBars = (customSearchTerm) => {
        const term = customSearchTerm !== undefined ? customSearchTerm : searchMenu;
        if (term.length > 0) {
            const filteredData = (returnList || []).filter((item) => deepSearch(item, term));
            setSearchResults(filteredData);
        } else {
            setSearchResults(returnList);
        }
    };

    const handleViewReturn = (returnData) => {
        setSelectedReturn(returnData);
        setShowViewModal(true);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Return#
                    </div>
                ),
                accessor: 'order_number',
                Cell: ({ row }) => <div>{row.original?.order_number}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Date
                    </div>
                ),
                accessor: 'created_at',
                Cell: ({ row }) => <div>{moment(row.original?.created_at).format('DD, MMM YYYY')}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Warehouse
                    </div>
                ),
                accessor: 'warehouse',
                Cell: ({ row }) => <div>{row.original?.warehouse?.code || '-'}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Sale Order#
                    </div>
                ),
                accessor: 'sale_order',
                Cell: ({ row }) => <div>{row.original?.sale_order?.order_number || '-'}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        View
                    </div>
                ),
                accessor: 'view',
                Cell: ({ row }) => (
                    <EyeIcon
                        onClick={() => handleViewReturn(row.original)}
                        style={{ cursor: 'pointer' }}
                    />
                )
            }
        ],
        [theme]
    );

    return (
        <DashboardWrapper>
            <div className={classNames(styles.UserContainer, 'text-start')}>
                <PoSearchBar setsearchMenu={setsearchMenu} searchMenu={searchMenu} suggestionsSource={returnList} SearchBars={SearchBars} />
                <div className={styles.Header}>
                    <Heading heading={'Return Orders'} headingStyle={styles.Heading} />
                    <div className="d-flex align-items-center">
                        <CustomButton
                            Icon={PlusIcon}
                            submitHandle={() => {
                                if (isRestrictActions(screenEnum.return, screenActionEnum.create)) {
                                    navigate(routeConstant.admin.returnOrder.addreturn.path);
                                }
                            }}
                            IconStyle={classNames('me-1', styles.IconStyle)}
                            text={'New Return'}
                            containerStyle={classNames(styles.AddProduct, 'ms-2')}
                        />
                    </div>
                </div>
                <GenericTable columns={columns} data={searchResults || []} TableWrapperStyle={classNames(styles.Table)} />
            </div>
            {isLoading && <OverlayLoading />}
            <ViewReturnModal
                show={showViewModal}
                handleClose={() => setShowViewModal(false)}
                returnData={selectedReturn}
            />
        </DashboardWrapper>
    );
}
